/**
 * ATT-2871 admin half — fetch the `tenantSettings` GraphQL query (shared core).
 *
 * Wraps the existing `graphqlClient` in `lib/util.ts`. Top-level Zod parse on
 * the response shape (defensive — codegen will eventually replace this with
 * generated types after ATT-2870 propagates to staging).
 *
 * Failure modes:
 *   - Network error / 5xx: Sentry exception + return null
 *   - Malformed response (missing schemaVersion / settings): Sentry warning + return null
 *
 * Caller (the hook) treats null as "fall back to bundled config" per design's
 * 5-layer fallback chain.
 *
 * ATT-2872 §4.1: this is the SHARED core consumed by both the ATT-2871 overlay
 * (`lib/tenantSettings/useTenantSettings.ts`) and the ATT-2872 discovery boot
 * (`lib/tenantDiscovery/useTenantDiscovery.ts`). The two modules apply their
 * own per-portal Zod allowlists on top of the raw rows this function returns.
 * Cross-referenced from `lib/tenantDiscovery/` (design.md §D3, pre-mortem #9).
 */

import * as Sentry from "@sentry/nextjs";
import { z } from "zod";
import { graphqlClient } from "../util";
import type { TenantSettingsResponse } from "./types";

/**
 * ATT-3201: nested registry routing object served by `tenantSettings.domain`
 * (backend ATT-3200 + ATT-3203). Optional + nullable so older backends (or
 * hosts the resolver can't map) parse cleanly — existing consumers ignore it,
 * the discovery boot + routing cache read it. `cookieDomain`/`publicBase` are
 * nullable strings; `portal`/`apiUrl` are strings.
 */
const TenantDomainSchema = z.object({
  cookieDomain: z.string().nullable(),
  portal: z.string(),
  apiUrl: z.string(),
  publicBase: z.string().nullable(),
});

const TenantSettingsResponseSchema = z.object({
  schemaVersion: z.number(),
  settings: z.array(
    z.object({
      key: z.string(),
      value: z.unknown(),
    }),
  ),
  domain: TenantDomainSchema.nullable().optional(),
});

const TENANT_SETTINGS_QUERY = `
  query TenantSettings {
    tenantSettings {
      schemaVersion
      settings {
        key
        value
      }
      domain {
        cookieDomain
        portal
        apiUrl
        publicBase
      }
    }
  }
`;

type TenantSettingsQueryResult = {
  tenantSettings: unknown;
};

export async function fetchTenantSettings(): Promise<TenantSettingsResponse | null> {
  let raw: TenantSettingsQueryResult;
  try {
    raw = await graphqlClient.request<TenantSettingsQueryResult>(TENANT_SETTINGS_QUERY);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { event: "tenant-settings-fetch-failed" },
    });
    return null;
  }

  const parsed = TenantSettingsResponseSchema.safeParse(raw?.tenantSettings);
  if (!parsed.success) {
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      scope.setTag("event", "tenant-settings-fetch-malformed");
      scope.setExtras({
        issues: parsed.error.issues.map((i) => ({
          path: i.path,
          message: i.message,
        })),
      });
      Sentry.captureMessage("tenant-settings-fetch-malformed", { level: "warning" });
    });
    return null;
  }

  return parsed.data;
}
